import { h as __h } from "preact";
import { signal } from "@preact/signals";
/** @import { Signal } from "@preact/signals"; */
import { Component, createRef, h } from "preact";
/** @import { ComponentChildren } from "preact"; */

import { assignEnterpriseMembersResponse, assignEnterpriseOrgMembersResponse, deleteEnterpriseMembersResponse, enterpriseMemberResponse, enterpriseOrgResponse, unassignEnterpriseOrgMembersResponse } from "~/backend/validate.js";
/** @import { RefObject } from "preact"; */
/** @import { orgRoleRefResponse, sessionEnterpriseResponse, userRefResponse, userRejectionResponse } from "~/backend/validate.js"; */
import { ApiError, query, authedApiRequest, paginatedResponse, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { SearchParams } from "~/lib/search.js";
import { alert } from "~/util/alert.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import { PromiseSignal } from "~/util/promise.js";
import Query from "~/util/query.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Dropdown from "~/component/Dropdown.js";
import Icon from "~/component/Icon.js";
import Modal from "~/component/Modal.js";
import Popover from "~/component/Popover.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import ChipList from "~/admin/component/ChipList.js";
import { ComboboxInput } from "~/admin/component/Combobox.js";
import MappingsLock from "~/admin/component/MappingsLock.js";
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";
import UserStack from "~/admin/component/UserStack.js";
import Roles from "~/admin/lib/roles.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Members.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Members.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class Members extends Component {
  checked = signal(/** @type {Map<string, enterpriseMemberResponse>} */new Map());
  paginate = new Pagination.State();
  sort = new Table.Sort({
    keys: ["created_at", "name", "email", "role"],
    by: "name"
  });
  users = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/members`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      q: SearchParams.q.value
    }],
    enabled: Boolean(this.props.enterprise),
    keepPreviousData: true
  }), {
    schema: paginatedResponse(enterpriseMemberResponse)
  });
  render() {
    return [__h(Crumb, {}, "Members"), __h("div", {
      "class": adminStyle.main
    }, __h(ComboboxInput, {
      query: SearchParams.q,
      debounce: 200
    }), __h("div", {
      "class": adminStyle.body
    }, __h(MembersActions, {
      enterpriseId: this.props.enterprise.id,
      checked: this.checked,
      users: this.users,
      scim: this.props.enterprise.is_scim_enabled,
      locked: this.props.enterprise.is_group_sync_enabled
    }), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, __h(MembersBody, {
      enterpriseId: this.props.enterprise.id,
      ownerId: this.props.enterprise.owner_id,
      locked: this.props.enterprise.is_group_sync_enabled,
      checked: this.checked,
      paginate: this.paginate,
      sort: this.sort,
      users: this.users
    }))))];
  }
}

/**
 * @extends {Component<MembersActionsProps>}
 *
 * @typedef {object} MembersActionsProps
 * @property {string} enterpriseId
 * @property {boolean} scim
 * @property {boolean} locked
 * @property {typeof Members.prototype.users} users
 * @property {typeof Members.prototype.checked} checked
 */
class MembersActions extends Component {
  /** @param {string} userId */
  #memberName(userId) {
    return this.props.checked.value.get(userId)?.name ?? userId;
  }

  /** @param {string} role */
  async #setRole(role) {
    const userIds = [...this.props.checked.value.keys()];
    const {
      data
    } = await authedApiRequest("PUT", `/v2/enterprises/${this.props.enterpriseId}/members/assign`, {
      json: {
        user_ids: userIds,
        role
      },
      schema: response(assignEnterpriseMembersResponse)
    });
    const len = data.assigned.length;
    if (len) {
      alert(`Changed ${len} ${pluralize(len, "member")} to ${Roles.label("enterprise", role)}.`, {
        type: "success"
      });
    }
    this.#alertRoleRejections(data.rejected);
    this.props.users.fetch();
    this.props.checked.value = new Map();
    if (identity.me && userIds.includes(identity.me.id)) {
      Query.invalidate(["/v2/session"], ["/ui/admin/orgs"]);
    }
  }

  /** @param {userRejectionResponse[]} rejected */
  #alertRoleRejections(rejected) {
    for (const reject of rejected) {
      const name = this.#memberName(reject.user_id);
      const message = reject.reason === "is_enterprise_owner" ? `${name} is the enterprise owner, so their role can't be changed.` : `${name}'s role couldn't be changed (${reject.reason}).`;
      alert(message, {
        type: "error",
        ms: Infinity
      });
    }
  }

  /** @type {RefObject<Modal>} */
  deleteMemberModalRef = createRef();
  async deleteMemberModalSubmit() {
    const {
      data
    } = await authedApiRequest("DELETE", `/v2/enterprises/${this.props.enterpriseId}/members`, {
      json: {
        user_ids: [...this.props.checked.value.keys()]
      },
      schema: response(deleteEnterpriseMembersResponse)
    });
    const len = data.deleted.length;
    if (len) alert(`Removed ${len} ${pluralize(len, "member")}`, {
      type: "success"
    });
    for (const reject of data.rejected) {
      const name = this.#memberName(reject.user_id);
      const message = reject.reason === "is_enterprise_owner" ? `${name} is the enterprise owner and can't be removed.` : `${name} couldn't be removed (${reject.reason}).`;
      alert(message, {
        type: "error",
        ms: Infinity
      });
    }
    this.props.users.fetch();
    this.props.checked.value = new Map();
    this.deleteMemberModalRef.current?.close();
  }

  /** @param {Map<string, boolean>} submission */
  async setOrgs(submission) {
    const enterpriseId = this.props.enterpriseId;
    const checkedUsers = [...this.props.checked.value.keys()];
    const requests = /** @type {Promise<{ updated: number; rejected: userRejectionResponse[] }>[]} */[];
    for (const [orgId, assign] of submission.entries()) {
      if (assign) {
        requests.push(authedApiRequest("PUT", `/v2/enterprises/${enterpriseId}/orgs/${orgId}/members/assign`, {
          json: {
            members: checkedUsers.map(user_id => ({
              user_id,
              role: "member"
            }))
          },
          schema: response(assignEnterpriseOrgMembersResponse)
        }).then(({
          data
        }) => ({
          updated: data.assigned.length,
          rejected: data.rejected
        })));
      } else {
        requests.push(authedApiRequest("PUT", `/v2/enterprises/${enterpriseId}/orgs/${orgId}/members/unassign`, {
          json: {
            user_ids: checkedUsers
          },
          schema: response(unassignEnterpriseOrgMembersResponse)
        }).then(({
          data
        }) => ({
          updated: data.unassigned.length,
          rejected: data.rejected
        })));
      }
    }
    try {
      const results = await Promise.all(requests);
      const updated = results.reduce((sum, result) => sum + result.updated, 0);
      if (updated) {
        const len = this.props.checked.value.size;
        alert(`Updated organizations for ${len} ${pluralize(len, "member")}`, {
          type: "success"
        });
      }
      for (const reject of results.flatMap(result => result.rejected)) {
        const name = this.#memberName(reject.user_id);
        alert(`Could not update ${name}'s organizations: ${reject.reason}`, {
          type: "error",
          ms: Infinity
        });
      }
      this.props.users.fetch();
      this.props.checked.value = new Map();
      if (identity.me && checkedUsers.includes(identity.me.id)) {
        Query.invalidate(["/v2/session"], ["/ui/admin/orgs"]);
      }
    } catch (err) {
      alert(ApiError.from(err).message, {
        type: "error"
      });
    }
  }
  render() {
    if (!identity.permission("enterprise", "members", "edit")) return null;
    const checked = this.props.checked.value;
    const bulk = checked.size > 0;
    const locked = this.props.locked;
    return [__h("menu", {
      "class": adminStyle.menu
    }, __h(MappingsLock, {
      locked: locked
    }, __h(Dropdown, {
      id: "member-assign-dropdown",
      placement: "bottom-start",
      items: Roles.enterprise.map(role => ({
        text: role.label,
        onSelect: async () => {
          const title = `Update ${checked.size} ${pluralize(checked.size, "member's role", "members' roles")} to ${role.label}?`;
          if (!(await Roles.confirm(role, title))) return;
          try {
            await this.#setRole(role.key);
          } catch (err) {
            alert(ApiError.from(err).message, {
              type: "error"
            });
          }
        }
      })),
      trigger: __h(Button, {
        disabled: !bulk || locked,
        popovertarget: "member-assign-dropdown",
        size: "small"
      }, "Change role")
    })), __h(MappingsLock, {
      locked: locked
    }, __h(ManageOrgsPopover, {
      enterpriseId: this.props.enterpriseId,
      checkedUsers: [...checked.values()],
      onSubmit: this.setOrgs.bind(this),
      trigger: __h(Button, {
        disabled: !bulk || locked,
        popovertarget: "manage-orgs-popover",
        size: "small"
      }, "Manage organizations")
    })), !this.props.scim && __h(Button, {
      disabled: !bulk,
      size: "small",
      kind: "negative",
      onClick: () => this.deleteMemberModalRef.current?.open()
    }, "Remove")), __h(DeleteMemberModal, {
      modalRef: this.deleteMemberModalRef,
      onSubmit: this.deleteMemberModalSubmit.bind(this),
      title: "Remove " + checked.size + " " + pluralize(checked.size, "member") + "?"
    })];
  }
}

/**
 * @extends {Component<ManageOrgsPopoverProps>}
 *
 * @typedef {object} ManageOrgsPopoverProps
 * @property {string} enterpriseId
 * @property {ComponentChildren} trigger
 * @property {(submission: Map<string, boolean>) => void} onSubmit
 * @property {enterpriseMemberResponse[]} checkedUsers
 */
class ManageOrgsPopover extends Component {
  orgs = query(() => ({
    key: [`/v2/enterprises/${this.props.enterpriseId}/orgs`, {
      limit: 9999
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(enterpriseOrgResponse)
  });

  /** @type {Signal<Map<string, boolean>>} */
  submission = signal(new Map());

  /** @param {string} orgId */
  getCheckboxState(orgId) {
    const checkedUsers = this.props.checkedUsers;
    const checkedUsersInOrg = checkedUsers.filter(user => user.orgs.findIndex(org => org.id === orgId) !== -1);
    return checkedUsersInOrg.length === 0 ? false : checkedUsersInOrg.length === checkedUsers.length ? true : "some";
  }
  render() {
    return __h(Popover, {
      onClose: () => {
        this.submission.value = new Map();
      },
      id: "manage-orgs-popover",
      placement: "bottom-start",
      trigger: this.props.trigger
    }, __h("div", {
      "class": css.manageOrgsPopover
    }, __h("ul", {}, h(Data, {
      loading: this.orgs.loading && !this.orgs.data,
      error: this.orgs.error,
      empty: this.orgs.data?.count === 0 && __h("div", {}, "No organizations"),
      children: this.orgs.data?.data.map(org => {
        const submission = this.submission.value.get(org.id);
        const original = this.getCheckboxState(org.id);
        return __h("li", {}, __h("input", {
          type: "checkbox",
          id: "mop-checkbox-" + org.id,
          checked: submission ?? original,
          indeterminate: (submission ?? original) === "some",
          onInput: () => {
            const input = (submission ?? original) !== true;
            if (input === original) {
              const val = new Map(this.submission.value);
              val.delete(org.id);
              this.submission.value = val;
            } else {
              this.submission.value = new Map(this.submission.value).set(org.id, input);
            }
          }
        }), __h("label", {
          "for": "mop-checkbox-" + org.id
        }, org.name));
      })
    })), __h("div", {
      "class": adminStyle.actions
    }, __h(Button, {
      size: "small",
      level: "primary",
      popovertarget: "manage-orgs-popover",
      popovertargetaction: "hide",
      disabled: this.submission.value.size === 0,
      onClick: () => this.props.onSubmit(this.submission.value)
    }, "Update organizations"), __h(Button, {
      size: "small",
      popovertarget: "manage-orgs-popover",
      popovertargetaction: "hide"
    }, "Cancel"))));
  }
}

/**
 * @extends {Component<DeleteMemberModalProps>}
 *
 * @typedef {object} DeleteMemberModalProps
 * @property {RefObject<Modal>} modalRef
 * @property {() => Promise<void>} onSubmit
 * @property {string} title
 */
class DeleteMemberModal extends Component {
  deleteMember = new PromiseSignal(/** @param {SubmitEvent} _ev */
  async _ev => {
    await this.props.onSubmit();
  });
  render() {
    const error = this.deleteMember.error && ApiError.from(this.deleteMember.error);
    return __h(Modal, {
      ref: this.props.modalRef,
      title: this.props.title
    }, __h("div", {
      "class": adminStyle.modal
    }, __h("p", {}, "This will remove the selected users from the enterprise."), __h("p", {
      "class": adminStyle.error
    }, error?.message), __h("form", {
      "class": adminStyle.actions,
      onSubmit: (/** @type {SubmitEvent} */ev) => {
        if (ev.submitter?.getAttribute("formmethod") === "dialog") return;
        ev.preventDefault();
        this.deleteMember.invoke(ev);
      }
    }, __h(Button, {
      type: "submit",
      level: "primary",
      kind: "negative",
      value: "delete",
      loading: this.deleteMember.loading
    }, "Remove"), __h(Button, {
      type: "submit",
      formmethod: "dialog",
      disabled: this.deleteMember.loading
    }, "Cancel"))));
  }
}

/**
 * @extends {Component<BodyProps>}
 *
 * @typedef {object} BodyProps
 * @property {string} enterpriseId
 * @property {string} ownerId
 * @property {boolean} locked
 * @property {typeof Members.prototype.checked} checked
 * @property {typeof Members.prototype.paginate} paginate
 * @property {typeof Members.prototype.sort} sort
 * @property {typeof Members.prototype.users} users
 */
class MembersBody extends Component {
  /**
   * @param {userRefResponse} user
   * @param {orgRoleRefResponse} org
   * @param {string} role
   */
  async changeRole(user, org, role) {
    const {
      data
    } = await authedApiRequest("PUT", `/v2/enterprises/${this.props.enterpriseId}/orgs/${org.id}/members/assign`, {
      json: {
        members: [{
          user_id: user.id,
          role
        }]
      },
      schema: response(assignEnterpriseOrgMembersResponse)
    });
    if (data.assigned.length) {
      alert(`${user.name}'s role in ${org.name} updated to ${Roles.label("org", role)}`, {
        type: "success"
      });
    }
    for (const reject of data.rejected) {
      alert(`Could not change ${user.name}'s role in ${org.name}: ${reject.reason}`, {
        type: "error",
        ms: Infinity
      });
    }
    this.props.users.fetch();
    if (user.id === identity.me?.id) {
      Query.invalidate(["/v2/session"], ["/ui/admin/orgs"]);
    }
  }
  render() {
    const users = this.props.users;
    const canEdit = identity.permission("enterprise", "members", "edit");
    const ownerId = this.props.ownerId;
    const rows = users.data?.data.map(user => ({
      key: user.id,
      label: user.name,
      value: user,
      columns: {
        user: __h(UserStack, {
          name: user.name,
          email: user.email
        }),
        role: user.id === ownerId ? "Owner" : Roles.label("enterprise", user.role),
        orgs: __h(ChipList, {}, user.orgs.map(org => __h("div", {
          key: org.id,
          "class": adminStyle.chip
        }, __h(Icon, {
          name: "users",
          size: 12
        }), org.name, __h(MappingsLock, {
          locked: this.props.locked
        }, __h(Dropdown, {
          id: "role-" + user.id + "-" + org.id,
          placement: "bottom-start",
          items: Roles.org.map(role => ({
            text: role.label,
            onSelect: async () => {
              const title = `Update ${user.name}'s role in ${org.name} to ${role.label}?`;
              if (!(await Roles.confirm(role, title))) return;
              try {
                await this.changeRole(user, org, role.key);
              } catch (err) {
                alert(ApiError.from(err).message, {
                  type: "error"
                });
              }
            }
          })),
          trigger: __h("button", {
            "class": css.roleButton,
            disabled: this.props.locked || !canEdit,
            popovertarget: "role-" + user.id + "-" + org.id
          }, Roles.label("org", org.role), !this.props.locked && canEdit && __h(Icon, {
            name: "down",
            size: 10
          }))
        })))))
      }
    }));
    return h(Data, {
      loading: users.loading && !users.data,
      error: users.error,
      children: h(/** @type {typeof Table<enterpriseMemberResponse>} */Table, {
        label: "member",
        checked: canEdit ? this.props.checked.value : undefined,
        onCheck: check => this.props.checked.value = check,
        headers: [{
          id: "user",
          label: "User",
          sort: "name"
        }, {
          id: "role",
          label: "Role",
          sort: "role"
        }, {
          id: "orgs",
          label: "Organizations"
        }],
        empty: SearchParams.q.value ? `No enterprise members match "${SearchParams.q.value}".` : "This enterprise has no members.",
        rows: rows ?? [],
        paginate: this.props.paginate.props(users),
        sort: this.props.sort.props()
      })
    });
  }
}